import React, { useState, useEffect } from 'react';
import { getAssetUrl, resolveCourseImageUrl } from '../../utils/helpers';
import { useLocation, Link } from 'react-router-dom';
import fiveHundredHourYttcImage from '../../assets/images/courses/500hr-yttc.webp';
import faceYogaTtcImage from '../../assets/images/courses/face-yoga-ttc.webp';
import dietAndNutritionImage from '../../assets/images/courses/diet-and-nutrition.webp';
import kidsYogaTtcImage from '../../assets/images/courses/95-hour-kids-yoga-ttc.webp';
import threeHundredHourYttcImage from '../../assets/images/courses/300hr-yttc.webp';
import twoHundredHourYttcImage from '../../assets/images/courses/200hr-yttc.webp';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import { useLeadModal } from '../../context/LeadModalContext';
import { useCountry } from '../../context/CountryContext';
import { apiGet } from '../../services/api';

const Programs = () => {
  const location = useLocation();
  const { openModal } = useLeadModal();
  const [activeFilter, setActiveFilter] = useState('all');
  const [dbCourses, setDbCourses] = useState([]);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Fetch public course fees & active coupon banners
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        if (coursesData) {
          setDbCourses(coursesData);
        }
      } catch (err) {
        console.error("Failed to load public courses fees:", err);
      }

      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error("Failed to load public coupons:", err);
      }
    }
    loadData();

    // Set up timer ticker
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle hash scrolling on load or changes, and set SEO metadata
  useEffect(() => {
    document.title = "Yoga Teacher Training Courses & Programs | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Explore 200, 300 & 500 Hour Yoga Teacher Training, Kids Yoga, Face Yoga, Diet & Nutrition and wellness programs from PranaFlow Academy in Rishikesh, India.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "Yoga Teacher Training Courses & Programs | PranaFlow Academy" },
      { property: 'og:description', content: "Explore 200, 300 & 500 Hour Yoga Teacher Training, Kids Yoga, Face Yoga, Diet & Nutrition and wellness programs from PranaFlow Academy in Rishikesh, India." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses" },
      { property: 'og:image', content: "https://pranaflowacademy.com/assets/rishikesh_hero.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Yoga Teacher Training Courses & Programs | PranaFlow Academy" },
      { name: 'twitter:description', content: "Explore 200, 300 & 500 Hour Yoga Teacher Training, Kids Yoga, Face Yoga, Diet & Nutrition and wellness programs from PranaFlow Academy in Rishikesh, India." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/assets/rishikesh_hero.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  // Dynamic JSON-LD Schema Injection
  useEffect(() => {
    const scriptId = 'jsonld-schema-programs';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const collectionSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": "https://pranaflowacademy.com/courses#webpage",
      "url": "https://pranaflowacademy.com/courses",
      "name": "Yoga Teacher Training & Wellness Courses",
      "description": "Explore PranaFlow Academy's yoga teacher training, face yoga, diet and nutrition, and wellness education courses.",
      "isPartOf": {
        "@id": "https://pranaflowacademy.com/#website"
      },
      "about": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "200 Hour Yoga Teacher Training Course",
            "url": "https://pranaflowacademy.com/courses/200-hour-yoga-teacher-training-rishikesh"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "300 Hour Yoga Teacher Training Course",
            "url": "https://pranaflowacademy.com/courses/300hr-yoga-ttc"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "500 Hour Yoga Teacher Training Course",
            "url": "https://pranaflowacademy.com/courses/500hr-yoga-ttc"
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "Face Yoga Teacher Training Course Online",
            "url": "https://pranaflowacademy.com/courses/face-yoga-teacher-training-course-online"
          },
          {
            "@type": "ListItem",
            "position": 5,
            "name": "Diet and Nutrition Course Online",
            "url": "https://pranaflowacademy.com/courses/diet-and-nutrition-course-online"
          },
          {
            "@type": "ListItem",
            "position": 6,
            "name": "95-Hour Kids Yoga Teacher Training",
            "url": "https://pranaflowacademy.com/courses/95-hour-kids-yoga-teacher-training"
          }
        ]
      }
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        }
      ]
    };

    script.textContent = JSON.stringify([collectionSchema, breadcrumbSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const courses = [
    {
      id: 'ttc200',
      category: 'teacher-training',
      title: '200 Hour Yoga Teacher Training',
      level: 'Teacher Training',
      levelClass: 'teacher-training',
      desc: 'Our premier globally certified professional program. Designed to establish deep roots in yoga teaching, it covers posture adjustments, structural human anatomy, pranayama breathing, yogic life ethics, and active class sequencing. Ideal for starting a professional career in teaching.',
      highlights: [
        'Ashtanga-Vinyasa Flow Sequences',
        'Hands-on Adjustments & Alignments',
        'Yogic Anatomy & Physiology Basics',
        'Yoga History & Philosophy Foundations'
      ],
      img: twoHundredHourYttcImage,
      image: twoHundredHourYttcImage,
      thumbnail: twoHundredHourYttcImage,
      meta: {
        duration: '200 Days (Online)',
        mode: 'Live Online',
        prereq: 'None (Open to all)',
        certification: 'Yoga Alliance RYT 200'
      },
      price: '$370',
      modalKey: 'ttc-200',
      customAction: true,
      customRoute: '/courses/200-hour-yoga-teacher-training-rishikesh'
    },
    {
      id: 'ttc300',
      category: 'teacher-training',
      title: '300 Hour Yoga Teacher Training',
      level: 'Teacher Training',
      levelClass: 'teacher-training',
      desc: 'Advance your teaching skills and elevate your yoga knowledge. This course is designed exclusively for 200h graduates. It focuses on advanced asana postures, structural bandhas, pranayama controls, and deep, comprehensive analyses of ancient Patanjali Sutras and Sanskrit scripts.',
      highlights: [
        'Advanced Asanas & Inversion Postures',
        'Deep Pranayama, Mudras & Bandhas',
        'Comprehensive Sutras & Upanishad Study',
        'Chakra Cleansing & Yoga Nidra Methods'
      ],
      img: threeHundredHourYttcImage,
      image: threeHundredHourYttcImage,
      thumbnail: threeHundredHourYttcImage,
      meta: {
        duration: '300 Days (Online)',
        mode: 'Live Online',
        prereq: 'RYT 200 Certificate',
        certification: 'Yoga Alliance RYT 300'
      },
      price: '$520',
      modalKey: 'ttc-300',
      customAction: true,
      customRoute: '/courses/300hr-yoga-ttc'
    },
    {
      id: 'ttc500',
      category: 'teacher-training',
      title: '500 Hour Yoga Teacher Training',
      level: 'Teacher Training',
      levelClass: 'teacher-training',
      desc: 'Step into the highest level of professional yoga education. Designed for certified yoga teachers who want to achieve the globally recognized RYT-500 qualification, this live online master-level course covers advanced practice, yoga therapy, deep philosophy, and master-level class design.',
      highlights: [
        'Advanced Hatha, Ashtanga & Vinyasa Flow Mastery',
        'Clinical Anatomy & Yoga Therapy Foundations',
        'Patanjali Yoga Sutras (All 4 Chapters) & Bhagavad Gita',
        'Master-Level Class Design & Teacher Development'
      ],
      img: fiveHundredHourYttcImage,
      image: fiveHundredHourYttcImage,
      thumbnail: fiveHundredHourYttcImage,
      meta: {
        duration: '500 Days (Online)',
        mode: 'Live Online',
        prereq: '200h YTT or Serious Practice',
        certification: 'Yoga Alliance RYT 500'
      },
      price: '$740',
      modalKey: 'ttc-500',
      customAction: true,
      customRoute: '/courses/500hr-yoga-ttc'
    },
    {
      id: 'kids',
      category: 'specialized',
      title: '95-Hour Kids Yoga Teacher Training',
      level: 'Specialized',
      levelClass: '',
      desc: 'Acquire specialized training to share the beautiful benefits of yoga with children. Learn child-friendly posture games, story-based sequencing, breathing exercises tailored for kids, and safe class environment control. Highly recommended for school teachers, parents, and yoga coaches.',
      highlights: [
        'Creating Fun Kids Yoga Games',
        'Storytelling & Asana Integrations',
        'Child Psychology & Behavior Guidance',
        'Child Mindfulness & Breathing Exercises'
      ],
      img: kidsYogaTtcImage,
      image: kidsYogaTtcImage,
      thumbnail: kidsYogaTtcImage,
      meta: {
        duration: '95 Hours',
        mode: 'Live Online / Offline Batch',
        prereq: 'Basic interest in child care',
        certification: 'PranaFlow Kids Certificate'
      },
      price: '$350',
      modalKey: 'kids-yoga',
      customAction: true,
      customRoute: '/courses/95-hour-kids-yoga-teacher-training'
    },
    {
      id: 'nutrition',
      category: 'specialized',
      title: 'Diet and Nutrition Course',
      level: 'Specialized',
      levelClass: '',
      desc: 'Learn the fundamentals of sattvic and holistic nutrition. Explore how yogic dietary wisdom combined with modern nutritional science promotes gut health, raises energy levels, improves immunity, and supports absolute metabolic wellness.',
      highlights: [
        'Principles of Sattvic, Rajasic & Tamasic Foods',
        'Modern Macronutrients & Micronutrients Science',
        'Gut Health, Digestion & Holistic Detoxification',
        'Designing Customized Yogic Meal & Diet Plans'
      ],
      img: dietAndNutritionImage,
      image: dietAndNutritionImage,
      thumbnail: dietAndNutritionImage,
      meta: {
        duration: '30 Hours (Online)',
        mode: 'Live Online',
        prereq: 'None (Open to all seekers)',
        certification: 'PranaFlow Nutrition Seal'
      },
      price: '$127',
      modalKey: 'nutrition',
      customAction: true,
      customRoute: '/courses/diet-and-nutrition-course-online'
    },
    {
      id: 'pcos',
      category: 'specialized',
      title: '3-Month PCOS & PCOD Management Program',
      level: 'Specialized',
      levelClass: '',
      desc: 'A structured 12-week guided wellness program designed to help you understand PCOS and develop healthier habits supporting metabolic, reproductive, and emotional wellbeing through nutrition, yoga, exercise, stress care, and menstrual tracking.',
      highlights: [
        'Complete 12-Week Structured Curriculum',
        'PCOS Nutrition Education & Practical Meal Planning',
        'Therapeutic Yoga, Pranayama & Exercise Routines',
        'Student Dashboard, Class Recordings & Revision Access'
      ],
      img: dietAndNutritionImage,
      image: dietAndNutritionImage,
      thumbnail: dietAndNutritionImage,
      meta: {
        duration: '3 Months / 12 Weeks',
        mode: 'Live Online Guided',
        prereq: 'Open to all women',
        certification: 'PranaFlow Program Certificate'
      },
      price: '$480',
      modalKey: 'pcos',
      customAction: true,
      customRoute: '/courses/pcos-pcod-management-program'
    },
    {
      id: 'face-yoga',
      category: 'specialized',
      title: 'Face Yoga Teacher Training Course',
      level: 'Specialized',
      levelClass: '',
      desc: 'Discover the art and science of facial rejuvenation. Learn targeted facial exercises, acupressure, self-massage, and lymphatic drainage to naturally tone muscles, reduce signs of ageing, and guide others as a certified Face Yoga teacher.',
      highlights: [
        '57 Facial Muscles Anatomy & Zone-by-Zone Exercises',
        'Facial Massage, Acupressure & Lymphatic Drainage Strokes',
        'Breathwork, Ayurvedic Skin Health & Holistic Skincare',
        'Face Yoga Class Architecture & Professional Teaching Methodology'
      ],
      img: faceYogaTtcImage,
      image: faceYogaTtcImage,
      thumbnail: faceYogaTtcImage,
      meta: {
        duration: '30 Hours (Online)',
        mode: 'Live Online',
        prereq: 'None (Open to all seekers)',
        certification: 'PranaFlow Face Yoga Certificate'
      },
      price: '$127',
      modalKey: 'face-yoga',
      customAction: true,
      customRoute: '/courses/face-yoga-teacher-training-course-online'
    }
  ];

  const { formatPrice } = useCountry();

  const findDbCourse = (staticCourse) => {
    if (!dbCourses || !Array.isArray(dbCourses) || dbCourses.length === 0) return null;

    const staticTitleNorm = staticCourse.title ? staticCourse.title.toLowerCase().trim() : '';
    const routeSlug = staticCourse.customRoute ? staticCourse.customRoute.replace(/^\//, '').toLowerCase().trim() : '';

    // 1. Direct title match
    let match = dbCourses.find(
      (c) => c.title && c.title.toLowerCase().trim() === staticTitleNorm
    );
    if (match) return match;

    // 2. Slug / customRoute match
    if (routeSlug) {
      match = dbCourses.find(
        (c) => (c.slug && c.slug.toLowerCase().trim() === routeSlug) ||
               (c.course_slug && c.course_slug.toLowerCase().trim() === routeSlug)
      );
      if (match) return match;
    }

    // 3. Substring match
    match = dbCourses.find(
      (c) => c.title && (c.title.toLowerCase().includes(staticTitleNorm) || staticTitleNorm.includes(c.title.toLowerCase()))
    );
    if (match) return match;

    // 4. Keyword matching (200, 300, 500, pcos, face yoga)
    if (staticTitleNorm.includes('200')) {
      match = dbCourses.find(c => c.title && c.title.includes('200'));
      if (match) return match;
    }
    if (staticTitleNorm.includes('300')) {
      match = dbCourses.find(c => c.title && c.title.includes('300'));
      if (match) return match;
    }
    if (staticTitleNorm.includes('500')) {
      match = dbCourses.find(c => c.title && c.title.includes('500'));
      if (match) return match;
    }
    if (staticTitleNorm.includes('pcos')) {
      match = dbCourses.find(c => c.title && c.title.toLowerCase().includes('pcos'));
      if (match) return match;
    }
    if (staticTitleNorm.includes('face')) {
      match = dbCourses.find(c => c.title && c.title.toLowerCase().includes('face'));
      if (match) return match;
    }
    if (staticTitleNorm.includes('kids')) {
      match = dbCourses.find(c => c.title && c.title.toLowerCase().includes('kids'));
      if (match) return match;
    }
    if (staticTitleNorm.includes('diet') || staticTitleNorm.includes('nutrition')) {
      match = dbCourses.find(c => c.title && (c.title.toLowerCase().includes('diet') || c.title.toLowerCase().includes('nutrition')));
      if (match) return match;
    }

    return null;
  };

  const getCoursePriceDisplay = (staticCourse) => {
    const match = findDbCourse(staticCourse);
    if (match) {
      const feeInr = parseFloat(match.fee_inr);
      if (!isNaN(feeInr) && feeInr > 0) {
        return formatPrice(feeInr, match);
      } else {
        return "Fee will be updated soon";
      }
    }
    return "Fee will be updated soon";
  };

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponForCourse = (staticCourse) => {
    const dbCourse = findDbCourse(staticCourse);
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    // 1. Check status
    if (match.status && match.status !== 'active') return null;

    // 2. Check show_on_pages flag (controlled by admin checkbox "Show this coupon code on program pages and individual course pages")
    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    // 3. Check banner_expiry
    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) {
        return null;
      }
    }
    // 4. Check banner_start
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) {
        return null;
      }
    }
    // 5. Check expiry_date
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) {
        return null;
      }
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Offer ended.';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days}d`);
    }
    parts.push(`${String(hours).padStart(2,'0')}h`);
    parts.push(`${String(minutes).padStart(2,'0')}m`);
    parts.push(`${String(seconds).padStart(2,'0')}s`);

    return parts.join(' ');
  };

  const formatExpiryDisplay = (dateStr) => {
    const d = parseDateSafe(dateStr);
    if (!d) return '';
    return d.toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  return (
    <div className="public-site">
      <PublicHeader />

      {/* =========================================================================
           PROGRAMS HERO
           ========================================================================= */}
      <section className="programs-hero">
        <div className="container">
          <h1 className="programs-hero-title">Yoga Teacher Training <span>Courses &amp; Programs</span></h1>
          <p className="programs-hero-desc">
            Explore Yoga Teacher Training courses and specialized wellness programs from PranaFlow Academy in Rishikesh, India. Choose from live online 200 Hour, 300 Hour and 500 Hour Yoga Teacher Training, Kids Yoga, Face Yoga, Diet &amp; Nutrition and other structured programs with clear curriculum, schedules, fees and certification details.
          </p>
        </div>
      </section>

      {/* =========================================================================
           TABS FILTERING SECTION
           ========================================================================= */}
      <section className="filter-tabs-section">
        <div className="container">
          <div className="tabs-wrapper">
            <button
              className={`tab-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All Courses
            </button>
            <button
              className={`tab-btn ${activeFilter === 'foundation' ? 'active' : ''}`}
              onClick={() => setActiveFilter('foundation')}
            >
              Foundation
            </button>
            <button
              className={`tab-btn ${activeFilter === 'teacher-training' ? 'active' : ''}`}
              onClick={() => setActiveFilter('teacher-training')}
            >
              Teacher Trainings
            </button>
            <button
              className={`tab-btn ${activeFilter === 'specialized' ? 'active' : ''}`}
              onClick={() => setActiveFilter('specialized')}
            >
              Specialized Courses
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================================
           COURSE CARDS — exact homepage pf-ref-course-card structure
           ========================================================================= */}
      <section className="detailed-courses-section">
        <div className="container">
          <div className="pf-courses-ref-grid">
            {courses.map((course) => {
              const isVisible = activeFilter === 'all' || course.category === activeFilter;
              const coupon = getBannerCouponForCourse(course);
              const match = findDbCourse(course);

              const feeInr = match ? parseFloat(match.fee_inr) : 0;
              let formattedOriginalPrice = getCoursePriceDisplay(course);
              let formattedDiscountedPrice = '';
              let percentageVal = 0;
              const isFallbackPrice = !formattedOriginalPrice || (typeof formattedOriginalPrice === 'string' && formattedOriginalPrice.toLowerCase().includes('fee will be updated soon'));

              if (coupon && match && feeInr > 0) {
                if (coupon.discount_type === 'percentage') {
                  percentageVal = Math.round(parseFloat(coupon.discount_value));
                  const discountedInr = feeInr * (1 - percentageVal / 100);
                  formattedDiscountedPrice = formatPrice(discountedInr, match);
                  formattedOriginalPrice = formatPrice(feeInr, match);
                } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                  const fixedVal = parseFloat(coupon.discount_value);
                  const discountedInr = Math.max(0, feeInr - fixedVal);
                  percentageVal = Math.round((fixedVal / feeInr) * 100);
                  formattedDiscountedPrice = formatPrice(discountedInr, match);
                  formattedOriginalPrice = formatPrice(feeInr, match);
                }
              }

              const hasDiscount = !!(coupon && match && feeInr > 0 && formattedDiscountedPrice);
              const couponCode = coupon ? (coupon.coupon_code || '') : '';

              const expiryDateStr = coupon ? (coupon.banner_expiry || coupon.expiry_date || null) : null;
              const countdownStr = expiryDateStr ? formatCountdown(expiryDateStr) : '';
              const showCountdown = !!(expiryDateStr && countdownStr && countdownStr !== 'Offer ended.');

              const isValidFee = !!(match && !isNaN(parseFloat(match.fee_inr)) && parseFloat(match.fee_inr) >= 0);
              const isFree = isValidFee && parseFloat(match.fee_inr) === 0;

              return (
                <div
                  key={course.id}
                  className="pf-ref-course-card"
                  id={course.id}
                  style={!isVisible ? { display: 'none' } : undefined}
                >
                  {/* Image with duration badge — identical to homepage */}
                  <div className="pf-ref-card-img-wrap">
                    <img
                      src={resolveCourseImageUrl(course)}
                      alt={course.title}
                      className="pf-ref-card-img"
                      loading="lazy"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    <span className="pf-ref-badge-duration">{course.meta.duration}</span>
                  </div>

                  {/* Card body — identical structure to homepage */}
                  <div className="pf-ref-card-body">
                    <h2 className="pf-ref-card-title">{course.title}</h2>
                    <p className="pf-ref-card-desc">{course.desc}</p>

                    {/* Pricing panel — same classes as homepage, extended with discount+countdown */}
                    <div className="pf-ref-pricing-panel">
                      {isFallbackPrice ? (
                        <div className="pf-ref-price-single" style={{ fontSize: '1.05rem' }}>
                          Fee will be updated soon.
                        </div>
                      ) : isFree ? (
                        <div>
                          <span className="pf-ref-price-label">COURSE TUITION FEE</span>
                          <div className="pf-ref-price-single">Free</div>
                        </div>
                      ) : hasDiscount ? (
                        <div>
                          <div className="pf-ref-price-badge-row">
                            <span className="pf-ref-price-badge">SPECIAL OFFER · {percentageVal}% OFF</span>
                          </div>
                          <div className="pf-ref-price-amount-row">
                            <span className="pf-ref-price-orig">{formattedOriginalPrice}</span>
                            <span className="pf-ref-price-disc">{formattedDiscountedPrice}</span>
                          </div>
                          <div className="pf-ref-price-coupon-note">
                            With code <strong className="pf-ref-code-tag">{couponCode}</strong> at checkout
                          </div>
                          {showCountdown && (
                            <div className="pf-courses-countdown">
                              <div className="pf-courses-countdown-until">
                                Offer valid until: {formatExpiryDisplay(expiryDateStr)}
                              </div>
                              <div className="pf-courses-countdown-timer">
                                <span className="pf-courses-countdown-label">Offer ends in:</span>
                                <span className="pf-courses-countdown-digits">{countdownStr}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="pf-ref-price-label">COURSE TUITION FEE</span>
                          <div className="pf-ref-price-single">{formattedOriginalPrice}</div>
                        </div>
                      )}
                    </div>

                    {/* Info rows — identical SVG icons and structure to homepage */}
                    <div className="pf-ref-info-rows">
                      <div className="pf-ref-info-row">
                        <span className="pf-ref-info-icon">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                            <line x1="8" y1="21" x2="16" y2="21"/>
                            <line x1="12" y1="17" x2="12" y2="21"/>
                          </svg>
                        </span>
                        <span className="pf-ref-info-text">Live Online & Classroom Training</span>
                      </div>
                      <div className="pf-ref-info-row">
                        <span className="pf-ref-info-icon">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                        </span>
                        <span className="pf-ref-info-text">{course.meta.duration} Training</span>
                      </div>
                      <div className="pf-ref-info-row">
                        <span className="pf-ref-info-icon">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                        </span>
                        <span className="pf-ref-info-text">{course.meta.certification}</span>
                      </div>
                    </div>

                    <div className="pf-course-promo-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                      <span>Every batch: First 5 students get an extra discount on fee!</span>
                    </div>

                    {/* CTA button — identical pill style to homepage pf-ref-card-btn */}
                    {course.customAction ? (
                      <Link to={course.customRoute} className="pf-ref-card-btn">
                        <span>View Course Details</span>
                        <span className="pf-ref-card-btn-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M7 17L17 7M17 7H7M17 7V17" />
                          </svg>
                        </span>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="pf-ref-card-btn"
                        onClick={() => openModal(course.modalKey)}
                      >
                        <span>Enroll Now</span>
                        <span className="pf-ref-card-btn-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M7 17L17 7M17 7H7M17 7V17" />
                          </svg>
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Programs;
